import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { AdminShell } from "@/components/AdminShell";
import { MemberDetailModal } from "@/pages/admin/MemberDetailModal";
import {
  Trash2, Search, RefreshCw, Users, Store, Award, Download, Upload, X,
  ChevronLeft, ChevronRight, CheckCircle2, Clock, AlertTriangle, Mail, MessageCircle, FileSpreadsheet, Eye, FileText, ShieldCheck, UserPlus,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Panel, Eyebrow, MonoLabel } from "@/components/Panel";
import { AdminLogin, useAdminSession } from "@/pages/admin/AdminLogin";
import { adminApi, apiErr } from "@/lib/authApi";
import { softLaunchApi } from "@/lib/softLaunchApi";

const TAB_META = {
  member:  { label: "Members",  icon: Users, testid: "tab-members" },
  stall:   { label: "Stalls",   icon: Store, testid: "tab-stalls" },
  sponsor: { label: "Sponsors", icon: Award, testid: "tab-sponsors" },
};
const STATUS_TONE = {
  paid: "var(--green)", payment_pending: "var(--amber)",
  draft: "var(--taupe)", cancelled: "var(--red-2)", refunded: "var(--muted)",
};
const clock = (iso) => (iso
  ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—");

export default function AdminRegistrations() {
  const { user, setUser, logout } = useAdminSession();
  if (user === null) return <AdminLogin onSuccess={setUser} />;
  if (!user || user.pending) return <AdminShell><div className="p-10 mono-label">Loading…</div></AdminShell>;
  return (
    <AdminShell user={user} onLogout={logout}>
      <RegistrationsConsole canDelete={user.role === "devadmin"} role={user.role} />
    </AdminShell>
  );
}

export function RegistrationsConsole({ canDelete = false, embedded = false, stallOnly = false, role = "admin" }) {
  const canManualRegister = role === "stall_manager" || role === "admin" || role === "devadmin";
  const [tab, setTab] = useState(stallOnly ? "stall" : "member");
  const [manualOpen, setManualOpen] = useState(false);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [status, setStatus] = useState("");
  const [chapter, setChapter] = useState("");
  const [region, setRegion] = useState("");
  const [category, setCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(null);
  const [resendingWa, setResendingWa] = useState(null);
  const [reconciling, setReconciling] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'email'|'whatsapp', reg }

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.name || "this registration"} (${r.phone})? This cannot be undone.`)) return;
    setDeleting(r.id);
    try {
      await adminApi.delete(`/admin/registrations/${r.id}`);
      toast.success("Registration deleted");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setDeleting(null); }
  };

  const bulkDelete = async () => {
    if (bulkConfirm !== "DELETE") return toast.error("Type DELETE to confirm");
    setBulkBusy(true);
    try {
      const r = await adminApi.post("/admin/registrations/bulk-delete", {
        ids: Array.from(selected), confirm: "DELETE",
      });
      toast.success(`Deleted ${r.data.deleted} registration${r.data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set()); setBulkOpen(false); setBulkConfirm("");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(false); }
  };

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await adminApi.get("/admin/registrations/list", {
        params: { type: tab, q: q || undefined, status: status || undefined,
                  chapter: chapter || undefined, region: region || undefined,
                  category: category || undefined,
                  date_from: dateFrom || undefined, date_to: dateTo || undefined,
                  page, page_size: 25 },
      });
      setData(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }, [tab, q, status, chapter, region, category, dateFrom, dateTo, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tab, status, chapter, region, category, dateFrom, dateTo]);

  const typeCounts = data?.type_counts || { member: 0, stall: 0, sponsor: 0 };
  const typeCountsFilled = data?.type_counts_filled || { member: 0, stall: 0, sponsor: 0 };
  const typeCountsFailed = data?.type_counts_failed || { member: 0, stall: 0, sponsor: 0 };
  const chapters = data?.filters?.chapters || [];
  const regions = data?.filters?.regions || [];
  const categories = data?.filters?.categories || [];
  const items = data?.items || [];
  const total = data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / 25));
  const anyFilter = q || status || chapter || region || category || dateFrom || dateTo;

  const clearFilters = () => { setQInput(""); setQ(""); setStatus(""); setChapter(""); setRegion(""); setCategory(""); setDateFrom(""); setDateTo(""); setPage(1); };

  const resend = async (regId) => {
    setResending(regId);
    try {
      const r = await softLaunchApi.resendInvoice(regId);
      toast.success(`Invoice ${r.invoice} emailed to ${r.email}`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setResending(null); }
  };

  const resendWa = async (regId) => {
    setResendingWa(regId);
    try {
      const r = await softLaunchApi.resendWhatsapp(regId);
      const ok = (r.enqueued || []).filter((e) => !e.error).length;
      toast.success(ok ? `WhatsApp re-queued to ${r.phone} (${ok} message${ok > 1 ? "s" : ""})` : `No WhatsApp templates could be queued`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setResendingWa(null); }
  };

  const reconcile = async (reg) => {
    setReconciling(reg.id);
    try {
      const r = await softLaunchApi.reconcile(reg.id);
      if (r.outcome === "reconciled") {
        toast.success(`Reconciled · ${r.payment_id} · ₹${((r.amount_paise || 0) / 100).toLocaleString("en-IN")} captured. Invoice + confirmation sent.`);
        load();
      } else if (r.outcome === "already_paid") {
        toast.info(r.message || "Already marked paid.");
        load();
      } else if (r.outcome === "check_failed") {
        toast.error("Couldn't reach Razorpay to verify. Please try again.");
      } else if (r.outcome === "no_attempts") {
        toast.warning("No payment attempts on file — nothing to reconcile.");
      } else {
        toast.warning("No successful payment found on Razorpay — record left untouched.");
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setReconciling(null); }
  };

  const exportCsv = () => {
    const headers = ["ID","Name","Phone","Email","Region","Chapter","Category","Type","Mode","Status","Invoice","Created"];
    const row = (r) => [r.id, r.name, r.phone, r.email || "", r.region || "", r.chapter || "", r.business_category || "",
      r.registration_type, r.payment_mode || "", r.status, r.invoice_number || "",
      r.created_at || ""].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",");
    const csv = [headers.join(","), ...items.map(row)].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `registrations-${tab}-page${page}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div>
      {!embedded && <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "var(--panel)", border: "1px solid var(--hairline)", color: "var(--text)", borderRadius: 0 }
      }} />}
      <MemberDetailModal regId={openId} onClose={() => setOpenId(null)} />
      <AlertDialog open={!!confirm} onOpenChange={(v) => { if (!v) setConfirm(null); }}>
        <AlertDialogContent data-testid="reg-resend-confirm" className="!bg-[var(--panel)] !border-[var(--hairline)] !rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif !text-[var(--text)]">
              {confirm?.kind === "whatsapp" ? "Resend WhatsApp confirmation?" : "Resend invoice email?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="mono-label !text-[var(--taupe)]">
              {confirm?.kind === "whatsapp"
                ? <>Re-queues the registration confirmation + QR pass to <span className="text-[var(--text)]">{confirm?.reg?.phone}</span> via AiSensy.</>
                : <>Re-generates the GST invoice PDF and emails it to <span className="text-[var(--text)]">{confirm?.reg?.email}</span>.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reg-resend-cancel" className="!rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reg-resend-ok"
              className="!bg-[var(--red)] !rounded-none"
              onClick={() => {
                const c = confirm;
                setConfirm(null);
                if (!c) return;
                if (c.kind === "whatsapp") resendWa(c.reg.id);
                else resend(c.reg.id);
              }}
            >
              {confirm?.kind === "whatsapp" ? "Send WhatsApp" : "Send Email"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkOpen} onOpenChange={(v) => { if (!v) { setBulkOpen(false); setBulkConfirm(""); } }}>
        <AlertDialogContent data-testid="reg-bulk-delete-confirm" className="!bg-[var(--panel)] !border-[var(--hairline)] !rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif !text-[var(--text)]">
              Delete {selected.size} registration{selected.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="mono-label !text-[var(--taupe)]">
              This permanently removes the selected row{selected.size === 1 ? "" : "s"} and their invoice meta.
              It does NOT refund any Razorpay payment captured for them.
              Type <span className="text-[var(--red-2)] font-bold">DELETE</span> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            data-testid="reg-bulk-delete-input"
            value={bulkConfirm}
            onChange={(e) => setBulkConfirm(e.target.value)}
            className="input-x mt-3"
            placeholder="Type DELETE"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reg-bulk-delete-cancel" className="!rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reg-bulk-delete-ok"
              disabled={bulkConfirm !== "DELETE" || bulkBusy}
              className="!bg-[var(--red)] !rounded-none disabled:opacity-50"
              onClick={(e) => { e.preventDefault(); bulkDelete(); }}
            >
              {bulkBusy ? "Deleting…" : `Delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className={embedded ? "" : "max-w-[1400px] mx-auto px-4 sm:px-8 py-10"}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <Eyebrow>{embedded ? "Devadmin · Registrations" : "Admin · Registrations"}</Eyebrow>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">Who's coming to Members Day.</h1>
          </div>
          <div className="flex items-center gap-3">
            {canManualRegister && (
              <button data-testid="reg-manual-btn" className="btn-ghost" onClick={() => setManualOpen(true)}>
                <UserPlus size={14} /> Register Manually
              </button>
            )}
            <ImportButton tab={tab} onDone={load} />
            <button data-testid="reg-export-btn" className="btn-ghost" onClick={exportCsv} disabled={!items.length}>
              <Download size={14} /> Export CSV
            </button>
            <button data-testid="reg-refresh-btn" className="btn-red" disabled={busy} onClick={load}>
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
          <ManualRegisterDialog
            open={manualOpen}
            onClose={() => setManualOpen(false)}
            initialType={tab}
            onDone={(type) => { setTab(type); load(); }}
          />
        </div>

        {/* Tabs — top: current filter count · bottom row: FILLED (draft) + PAYMENT FAILED (blinking) */}
        <div className="mt-8 flex hairline overflow-x-auto" data-testid="reg-tabs">
          {Object.entries(TAB_META).filter(([k]) => !stallOnly || k === "stall").map(([k, meta]) => (
            <button
              key={k}
              type="button"
              data-testid={meta.testid}
              onClick={() => setTab(k)}
              className="flex-1 min-w-[200px] px-5 py-4 text-left border-r border-[var(--hairline)] last:border-r-0 transition-colors"
              style={{
                background: tab === k ? "var(--panel)" : "transparent",
                borderTop: tab === k ? "2px solid var(--red)" : "2px solid transparent",
              }}
            >
              <div className="flex items-center gap-2">
                <meta.icon size={14} style={{ color: tab === k ? "var(--red)" : "var(--taupe)" }} />
                <span className="mono-label" style={{ color: tab === k ? "var(--red)" : "var(--taupe)" }}>{meta.label}</span>
              </div>
              <div className="font-serif text-3xl mt-2" style={{ color: tab === k ? "var(--text)" : "var(--body)" }}>
                {typeCounts[k].toLocaleString("en-IN")}
              </div>
              <div className="mt-3 flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5" title="Form filled, payment not yet attempted">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--taupe)" }} />
                  <span className="mono-label" style={{ color: "var(--taupe)" }}>FILLED</span>
                  <span className="mono-label" style={{ color: "var(--body)" }} data-testid={`tab-${k}-filled-count`}>
                    {typeCountsFilled[k].toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="flex items-center gap-1.5" title="Payment attempted but not completed — follow up with these members">
                  <span
                    className={typeCountsFailed[k] > 0 ? "w-1.5 h-1.5 rounded-full reg-blink" : "w-1.5 h-1.5 rounded-full"}
                    style={{ background: "var(--red)" }}
                  />
                  <span className="mono-label" style={{ color: "var(--red-2)" }}>FAILED</span>
                  <span
                    className={typeCountsFailed[k] > 0 ? "mono-label reg-blink" : "mono-label"}
                    style={{ color: typeCountsFailed[k] > 0 ? "var(--red)" : "var(--body)", fontWeight: typeCountsFailed[k] > 0 ? 700 : 400 }}
                    data-testid={`tab-${k}-failed-count`}
                  >
                    {typeCountsFailed[k].toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <Panel className="mt-6 p-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--taupe)" }} />
              <input
                data-testid="reg-search"
                className="input-x !pl-9 w-full"
                placeholder="Search name, phone, email, invoice…"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
              />
            </div>
            <select data-testid="reg-status-filter" className="input-x !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="paid">Paid</option>
              <option value="payment_pending">Payment failed</option>
              <option value="draft">Filled (no payment)</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
            </select>
            <select data-testid="reg-region-filter" className="input-x !w-auto" value={region} onChange={(e) => setRegion(e.target.value)}>
              <option value="">All regions</option>
              {regions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="reg-chapter-filter" className="input-x !w-auto" value={chapter} onChange={(e) => setChapter(e.target.value)}>
              <option value="">All chapters</option>
              {chapters.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="reg-category-filter" className="input-x !w-auto" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex items-center gap-1" title="Registered between">
              <input
                data-testid="reg-date-from"
                type="date"
                className="input-x !w-auto !py-1.5 !text-xs"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From"
              />
              <span className="mono-label" style={{ color: "var(--taupe)" }}>→</span>
              <input
                data-testid="reg-date-to"
                type="date"
                className="input-x !w-auto !py-1.5 !text-xs"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="To"
              />
            </div>
            {anyFilter && (
              <button data-testid="reg-clear-filters" className="btn-ghost !py-2 !px-3 text-xs" onClick={clearFilters}>
                <X size={12} /> Clear
              </button>
            )}
            <MonoLabel className="ml-auto" style={{ color: "var(--taupe)" }}>
              {total.toLocaleString("en-IN")} MATCHING {TAB_META[tab].label.toUpperCase()}
            </MonoLabel>
            {canDelete && selected.size > 0 && (
              <button
                data-testid="reg-bulk-delete"
                className="btn-ghost !py-2 !px-3 text-xs"
                style={{ color: "var(--red-2)", borderColor: "var(--red-2)" }}
                onClick={() => { setBulkConfirm(""); setBulkOpen(true); }}
              >
                <Trash2 size={12} /> Delete {selected.size} selected
              </button>
            )}
          </div>
        </Panel>

        {/* Table */}
        <Panel className="mt-6 p-5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left mono-label" style={{ color: "var(--taupe)" }}>
                  {canDelete && (
                    <th className="py-3 pr-2 w-8">
                      <input type="checkbox" data-testid="reg-select-all"
                        checked={items.length > 0 && items.every((r) => selected.has(r.id))}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) items.forEach((r) => next.add(r.id));
                          else items.forEach((r) => next.delete(r.id));
                          setSelected(next);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  )}
                  <th className="py-3 pr-3">Who</th>
                  <th className="py-3 pr-3">Phone</th>
                  <th className="py-3 pr-3">Region</th>
                  <th className="py-3 pr-3">Chapter</th>
                  <th className="py-3 pr-3">Category</th>
                  <th className="py-3 pr-3">Mode</th>
                  <th className="py-3 pr-3">Status</th>
                  <th className="py-3 pr-3">Registered</th>
                  <th className="py-3">Invoice</th>
                  {canDelete && <th className="py-3 text-right">Delete</th>}
                </tr>
              </thead>
              <tbody>
                {!data && (
                  <tr><td colSpan={10} className="py-10 text-center mono-label" data-testid="reg-loading">Loading…</td></tr>
                )}
                {data && items.length === 0 && (
                  <tr><td colSpan={10} className="py-14 text-center mono-label" style={{ color: "var(--taupe)" }} data-testid="reg-empty">
                    No {TAB_META[tab].label.toLowerCase()} match these filters yet.
                  </td></tr>
                )}
                {items.map((r, i) => (
                  <tr key={r.id} className="hairline-t cursor-pointer hover:bg-[var(--panel-2)] transition-colors" data-testid={`reg-row-${i}`} onClick={() => setOpenId(r.id)}>
                    {canDelete && (
                      <td className="py-3 pr-2 w-8" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" data-testid={`reg-select-${i}`}
                          checked={selected.has(r.id)}
                          onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(r.id); else next.delete(r.id);
                            setSelected(next);
                          }}
                        />
                      </td>
                    )}
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {r.photo_url ? (
                          <img src={r.photo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${r.photo_url}` : r.photo_url}
                               alt="" className="w-9 h-9 object-cover border border-[var(--hairline)]" />
                        ) : (
                          <div className="w-9 h-9 border border-[var(--hairline)] bg-[var(--panel-2)] flex items-center justify-center font-serif text-xs" style={{ color: "var(--red-2)" }}>
                            {(r.name || "·").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="truncate">{r.name || "—"}</div>
                          {r.custom_fields?.contact_person && (
                            <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>Contact · {r.custom_fields.contact_person}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-3 mono-label whitespace-nowrap" style={{ color: "var(--body)" }} data-testid={`reg-phone-${i}`}>{r.phone || "—"}</td>
                    <td className="py-3 pr-3 mono-label" style={{ color: "var(--body)" }}>{r.region || "—"}</td>
                    <td className="py-3 pr-3 mono-label" style={{ color: "var(--body)" }}>{r.chapter || "—"}</td>
                    <td className="py-3 pr-3 mono-label truncate max-w-[180px]" style={{ color: "var(--body)" }}>{r.business_category || "—"}</td>
                    <td className="py-3 pr-3 mono-label" data-testid={`reg-mode-${i}`} style={{ color: "var(--body)" }}>
                      {r.payment_mode === "cash" ? "Cash" : r.payment_mode === "online" ? "Online" : "—"}
                    </td>
                    <td className="py-3 pr-3 mono-label" style={{ color: STATUS_TONE[r.status] || "var(--text)" }} data-testid={`reg-status-${i}`}>
                      {r.status === "paid"
                        ? <CheckCircle2 size={13} className="inline mr-1" />
                        : r.status === "cancelled" || r.status === "refunded"
                          ? <AlertTriangle size={13} className="inline mr-1" />
                          : <Clock size={13} className="inline mr-1" />}
                      {r.status}
                    </td>
                    <td className="py-3 pr-3 mono-label" style={{ color: "var(--taupe)" }}>{clock(r.paid_at || r.created_at)}</td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        {r.status === "paid" && r.invoice_number ? (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${r.id}.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`reg-invoice-link-${i}`}
                            className="mono-label truncate inline-flex items-center gap-1"
                            style={{ color: "var(--red-2)", textDecoration: "underline", textUnderlineOffset: "3px" }}
                            title="Preview invoice PDF"
                          >
                            <FileText size={12} /> {r.invoice_number}
                          </a>
                        ) : (
                          <span className="mono-label truncate" style={{ color: r.invoice_number ? "var(--text)" : "var(--muted)" }}>
                            {r.invoice_number || "—"}
                          </span>
                        )}
                        {r.status === "paid" && r.email && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                data-testid={`reg-resend-${i}`}
                                onClick={(e) => { e.stopPropagation(); setConfirm({ kind: "email", reg: r }); }}
                                disabled={resending === r.id}
                                aria-label="Resend invoice email"
                                className="btn-ghost !py-1 !px-2 mono-label"
                              >
                                {resending === r.id ? <Clock size={13} className="animate-spin" /> : <Mail size={13} />}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Resend Email</TooltipContent>
                          </Tooltip>
                        )}
                        {r.status === "paid" && r.phone && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                data-testid={`reg-resend-wa-${i}`}
                                onClick={(e) => { e.stopPropagation(); setConfirm({ kind: "whatsapp", reg: r }); }}
                                disabled={resendingWa === r.id}
                                aria-label="Resend WhatsApp confirmation"
                                className="btn-ghost !py-1 !px-2 mono-label"
                                style={{ color: "var(--green)" }}
                              >
                                {resendingWa === r.id ? <Clock size={13} className="animate-spin" /> : <MessageCircle size={13} />}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Resend WhatsApp</TooltipContent>
                          </Tooltip>
                        )}
                        {["payment_pending", "failed", "draft"].includes(r.status) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                data-testid={`reg-reconcile-${i}`}
                                onClick={(e) => { e.stopPropagation(); reconcile(r); }}
                                disabled={reconciling === r.id}
                                aria-label="Reconcile with Razorpay"
                                className="btn-ghost !py-1 !px-2 mono-label inline-flex items-center gap-1 whitespace-nowrap"
                                style={{ color: "var(--amber)", borderColor: "var(--amber)" }}
                              >
                                {reconciling === r.id ? <Clock size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
                                <span className="text-[11px] tracking-wider">RECONCILE</span>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Check Razorpay &amp; mark paid if captured</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                    {canDelete && (
                      <td className="py-3 text-right">
                        <button data-testid={`reg-delete-${i}`} disabled={deleting === r.id}
                          onClick={(e) => { e.stopPropagation(); remove(r); }}
                          className="btn-ghost !py-1 !px-2 mono-label hover:!border-[var(--red)]" title="Delete registration" style={{ color: "var(--red-2)" }}>
                          <Trash2 size={12} /> {deleting === r.id ? "…" : "Delete"}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
              <MonoLabel style={{ color: "var(--taupe)" }}>
                PAGE {page} OF {pageCount} · {total.toLocaleString("en-IN")} TOTAL
              </MonoLabel>
              <div className="flex items-center gap-2">
                <button
                  data-testid="reg-prev"
                  disabled={page <= 1 || busy}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="btn-ghost !py-1.5 !px-3 text-xs disabled:opacity-40"
                >
                  <ChevronLeft size={12} /> Prev
                </button>
                <button
                  data-testid="reg-next"
                  disabled={page >= pageCount || busy}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  className="btn-ghost !py-1.5 !px-3 text-xs disabled:opacity-40"
                >
                  Next <ChevronRight size={12} />
                </button>
              </div>
            </div>
          )}
        </Panel>
      </div>
    </div>
    </TooltipProvider>
  );
}

// ---------------- Register Manually (walk-in cash) ----------------
function ManualRegisterDialog({ open, onClose, initialType = "member", onDone }) {
  const [type, setType] = useState(initialType);
  const [regions, setRegions] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [members, setMembers] = useState([]);
  const [region, setRegion] = useState("");
  const [chapter, setChapter] = useState("");
  const [memberId, setMemberId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [stallName, setStallName] = useState("");
  const [sponsorTier, setSponsorTier] = useState("");
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const TYPE_META = {
    member:  { label: "Member",  icon: Users, testid: "manual-type-member" },
    stall:   { label: "Stall",   icon: Store, testid: "manual-type-stall" },
    sponsor: { label: "Sponsor", icon: Award, testid: "manual-type-sponsor" },
  };

  useEffect(() => {
    if (!open) return;
    setType(initialType);
    setResult(null);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/regions`)
      .then((r) => setRegions(r.data?.items || []))
      .catch(() => setRegions([]));
  }, [open, initialType]);

  useEffect(() => {
    const r = regions.find((x) => x.region === region);
    setChapters(r?.chapters || []);
    if (region && r && !r.chapters.includes(chapter)) setChapter("");
  }, [region, regions]);

  useEffect(() => {
    if (!region || !chapter) { setMembers([]); return; }
    setLoadingMembers(true);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/directory/members`, {
      params: { region, chapter },
    })
      .then((r) => setMembers(r.data?.items || []))
      .catch(() => setMembers([]))
      .finally(() => setLoadingMembers(false));
  }, [region, chapter]);

  const pickMember = (mid) => {
    setMemberId(mid);
    if (!mid) return;
    const m = members.find((x) => x.id === mid);
    if (!m) return;
    setName(m.name || "");
    const p = (m.phone || "").replace(/\D/g, "");
    if (p.length >= 10) setPhone(p.slice(-10));
    setEmail(m.email || "");
    // category stored as "Primary > Sub > Sub" — show the top-level for convenience
    setCategory((m.category || "").split(" > ")[0] || m.category || "");
  };

  const reset = () => {
    setRegion(""); setChapter(""); setMemberId(""); setName(""); setPhone("");
    setEmail(""); setCategory(""); setAmount(""); setStallName(""); setSponsorTier("");
    setMembers([]); setResult(null);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!region) return toast.error("Pick a region");
    if (!chapter) return toast.error("Pick a chapter");
    if (!name.trim()) return toast.error("Enter the member's name");
    if (!/^\d{10}$/.test(phone)) return toast.error("Enter a valid 10-digit mobile number");
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter the amount in rupees");
    setBusy(true);
    try {
      const body = {
        type, name: name.trim(), phone, email: email.trim() || null,
        region, chapter, business_category: category.trim() || null,
        amount_rupees: amt,
      };
      if (type === "stall")   body.stall_name = (stallName || name).trim();
      if (type === "sponsor") body.sponsor_tier = sponsorTier.trim() || null;
      if (memberId)           body.member_id = memberId;
      const r = await adminApi.post("/admin/registrations/manual", body);
      setResult(r.data);
      const waCount = (r.data.whatsapp || []).filter((w) => w.status).length;
      toast.success(
        `${r.data.updated ? "Updated" : "Registered"} · Invoice ${r.data.invoice?.number}` +
        (waCount ? ` · WhatsApp queued (${waCount})` : "")
      );
      onDone?.(type);
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose?.(); reset(); } }}>
      <DialogContent className="!bg-[var(--panel)] !border-[var(--hairline)] !rounded-none max-w-[640px] p-0 max-h-[92vh] overflow-y-auto" data-testid="manual-register-dialog">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl !text-[var(--text)]">
              Register manually · walk-in
            </DialogTitle>
            <DialogDescription className="mono-label mt-1 !text-[var(--taupe)]">
              Collects cash at the desk, marks the row paid on the spot, and mints a non-GST invoice.
            </DialogDescription>
          </DialogHeader>

          {!result && (
            <form onSubmit={submit} className="mt-5 space-y-5">
              {/* Type toggle */}
              <div className="hairline flex" data-testid="manual-type-toggle">
                {Object.entries(TYPE_META).map(([k, meta]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={meta.testid}
                    onClick={() => setType(k)}
                    className="flex-1 px-4 py-2.5 border-r border-[var(--hairline)] last:border-r-0 transition-colors flex items-center justify-center gap-2 mono-label"
                    style={{
                      background: type === k ? "var(--red)" : "transparent",
                      color: type === k ? "#fff" : "var(--taupe)",
                    }}
                  >
                    <meta.icon size={13} /> {meta.label}
                  </button>
                ))}
              </div>

              {/* Region + Chapter */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label" style={{ color: "var(--taupe)" }}>Region</label>
                  <select data-testid="manual-region" className="input-x mt-2 w-full" value={region} onChange={(e) => { setRegion(e.target.value); setChapter(""); setMemberId(""); }}>
                    <option value="">Pick a region…</option>
                    {regions.map((r) => <option key={r.region} value={r.region}>{r.region}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono-label" style={{ color: "var(--taupe)" }}>Chapter</label>
                  <select data-testid="manual-chapter" className="input-x mt-2 w-full" value={chapter} onChange={(e) => { setChapter(e.target.value); setMemberId(""); }} disabled={!region}>
                    <option value="">{region ? "Pick a chapter…" : "Pick a region first"}</option>
                    {chapters.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Member picker */}
              <div>
                <label className="mono-label" style={{ color: "var(--taupe)" }}>
                  Member {region && chapter && !loadingMembers ? `· ${members.length} on file` : ""}
                </label>
                <select data-testid="manual-member" className="input-x mt-2 w-full" value={memberId} onChange={(e) => pickMember(e.target.value)} disabled={!chapter || loadingMembers}>
                  <option value="">{loadingMembers ? "Loading members…" : chapter ? "— Pick from directory or leave blank —" : "Pick region + chapter first"}</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{m.name}{m.category ? ` · ${(m.category.split(" > ")[0] || "").slice(0, 28)}` : ""}</option>)}
                </select>
              </div>

              {/* Identity */}
              <div className="grid grid-cols-2 gap-3">
                <div className={type === "stall" ? "col-span-2" : ""}>
                  <label className="mono-label" style={{ color: "var(--taupe)" }}>
                    {type === "stall" ? "Contact person name" : "Full name"}
                  </label>
                  <input data-testid="manual-name" className="input-x mt-2 w-full" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                {type === "stall" && (
                  <div className="col-span-2">
                    <label className="mono-label" style={{ color: "var(--taupe)" }}>Stall / Company name</label>
                    <input data-testid="manual-stall-name" className="input-x mt-2 w-full" value={stallName} onChange={(e) => setStallName(e.target.value)} placeholder="Shown on the invoice" />
                  </div>
                )}
                <div>
                  <label className="mono-label" style={{ color: "var(--taupe)" }}>Mobile (10 digits)</label>
                  <input data-testid="manual-phone" inputMode="numeric" pattern="[0-9]*" maxLength={10} className="input-x mt-2 w-full" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} />
                </div>
                <div>
                  <label className="mono-label" style={{ color: "var(--taupe)" }}>Email (optional)</label>
                  <input data-testid="manual-email" type="email" className="input-x mt-2 w-full" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className={type === "sponsor" ? "" : "col-span-2"}>
                  <label className="mono-label" style={{ color: "var(--taupe)" }}>Business category (optional)</label>
                  <input data-testid="manual-category" className="input-x mt-2 w-full" value={category} onChange={(e) => setCategory(e.target.value)} />
                </div>
                {type === "sponsor" && (
                  <div>
                    <label className="mono-label" style={{ color: "var(--taupe)" }}>Sponsor tier</label>
                    <input data-testid="manual-sponsor-tier" className="input-x mt-2 w-full" value={sponsorTier} onChange={(e) => setSponsorTier(e.target.value)} placeholder="Gold / Silver / Bronze…" />
                  </div>
                )}
              </div>

              {/* Amount */}
              <div>
                <label className="mono-label" style={{ color: "var(--taupe)" }}>Amount collected (₹)</label>
                <input data-testid="manual-amount" type="number" min="1" step="1" className="input-x mt-2 w-full font-serif text-2xl" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 15000" />
                <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>Non-GST · cash · marked paid immediately</div>
              </div>

              <div className="flex items-center gap-3 justify-end pt-2">
                <button type="button" className="btn-ghost" onClick={() => { onClose?.(); reset(); }}>Cancel</button>
                <button type="submit" data-testid="manual-submit" className="btn-red" disabled={busy}>
                  <UserPlus size={14} /> {busy ? "Registering…" : "Register & generate invoice"}
                </button>
              </div>
            </form>
          )}

          {result && (
            <div className="mt-6" data-testid="manual-register-result">
              <div className="hairline p-5 flex items-center gap-3" style={{ background: "rgba(63,191,127,0.06)" }}>
                <CheckCircle2 size={22} style={{ color: "var(--green)" }} />
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-lg">{result.updated ? "Registration updated" : "Walk-in registered"}</div>
                  <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>
                    {result.type.toUpperCase()} · {result.name} · ₹{(result.amount_paise / 100).toLocaleString("en-IN")} · Invoice {result.invoice?.number}
                  </div>
                  {Array.isArray(result.whatsapp) && result.whatsapp.length > 0 && (
                    <div className="mono-label mt-1 flex items-center gap-1.5" style={{ color: "var(--green)" }} data-testid="manual-wa-status">
                      <MessageCircle size={12} />
                      {(() => {
                        const queued = result.whatsapp.filter((w) => w.status).map((w) => w.template_key);
                        const failed = result.whatsapp.filter((w) => w.error).length;
                        if (queued.length && !failed) return `WhatsApp queued · ${queued.join(" + ")} · ${result.phone}`;
                        if (queued.length && failed) return `WhatsApp partial · ${queued.length} queued, ${failed} skipped`;
                        return `WhatsApp skipped · check template setup`;
                      })()}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3 justify-end flex-wrap">
                <a data-testid="manual-invoice-link"
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${result.registration_id}.pdf`}
                  target="_blank" rel="noopener noreferrer" className="btn-ghost">
                  <FileText size={14} /> Open invoice PDF
                </a>
                <button className="btn-ghost" onClick={reset}>Register another</button>
                <button className="btn-red" onClick={() => { onClose?.(); reset(); }}>Done</button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Excel / CSV Import ----------------
function ImportButton({ tab, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const TAB_LABEL = { member: "members", stall: "stalls", sponsor: "sponsors" };

  const reset = () => { setFile(null); setPreview(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("bni-admin-token") || localStorage.getItem("bni-devadmin-token");
    // Cache-bust so the browser/service-worker never returns a stale template
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/import-template?type=${tab}&_=${Date.now()}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" });
    if (!res.ok) { toast.error("Could not download template"); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `registrations-${tab}-template-${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const upload = async (dryRun) => {
    if (!file) { toast.error("Choose a .xlsx or .csv file first"); return; }
    setBusy(true);
    const token = localStorage.getItem("bni-admin-token") || localStorage.getItem("bni-devadmin-token");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/import?type=${tab}&dry_run=${dryRun}`;
      const res = await fetch(url, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) {
        const msg = data?.detail?.errors?.[0]?.message || data?.detail || "Import failed";
        toast.error(typeof msg === "string" ? msg : "Import failed");
        setBusy(false); return;
      }
      if (dryRun) {
        setPreview(data);
      } else {
        setResult(data);
        toast.success(`Imported ${data.inserted} new, updated ${data.updated}, skipped ${data.skipped}`);
        onDone?.();
      }
    } catch (e) { toast.error(String(e.message || e)); } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="reg-import-btn" className="btn-ghost" onClick={() => setOpen(true)}>
        <Upload size={14} /> Import Excel
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="!bg-[var(--panel)] !border-[var(--hairline)] !rounded-none max-w-[640px] p-0" data-testid="reg-import-dialog">
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl !text-[var(--text)]">
                Import {TAB_LABEL[tab]} from Excel
              </DialogTitle>
              <DialogDescription className="mono-label mt-1 !text-[var(--taupe)]">
                Upload a .XLSX or .CSV · rows upserted by phone · marked as Paid via Cash · supports Region + Photo URL columns
              </DialogDescription>
            </DialogHeader>

            {!result && (
              <>
                <div className="mt-6 hairline p-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm">1. Download the template</div>
                    <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>
                      Pre-filled headers for {TAB_LABEL[tab]} · example row included
                    </div>
                  </div>
                  <button data-testid="reg-import-template" className="btn-ghost shrink-0" onClick={downloadTemplate}>
                    <FileSpreadsheet size={14} /> Template
                  </button>
                </div>

                <div className="mt-4 hairline p-4">
                  <div className="text-sm">2. Fill it in and upload</div>
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <input
                      ref={inputRef}
                      data-testid="reg-import-file"
                      type="file"
                      accept=".xlsx,.xlsm,.csv"
                      onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                      className="text-xs text-[var(--body)] file:mr-3 file:py-2 file:px-3 file:border file:border-[var(--hairline)] file:bg-[var(--panel-2)] file:text-[var(--text)] file:text-xs"
                    />
                    {file && (
                      <span className="mono-label" style={{ color: "var(--taupe)" }}>
                        {file.name} · {(file.size / 1024).toFixed(1)} KB
                      </span>
                    )}
                  </div>
                </div>

                {preview && (
                  <div className="mt-4 hairline p-4" data-testid="reg-import-preview">
                    <div className="flex items-center gap-2 mono-label" style={{ color: "var(--green)" }}>
                      <Eye size={13} /> DRY RUN · {preview.inserted} would import, {preview.skipped} would skip
                    </div>
                    {preview.errors?.length > 0 && (
                      <div className="mt-3 max-h-[180px] overflow-y-auto text-xs" style={{ color: "var(--amber)" }}>
                        {preview.errors.slice(0, 10).map((e, i) => (
                          <div key={i} className="mono-label">Row {e.row}: {e.message}</div>
                        ))}
                        {preview.errors.length > 10 && (
                          <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
                            +{preview.errors.length - 10} more…
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mono-label mt-3" style={{ color: "var(--taupe)" }}>
                      Default tier: {preview.default_tier?.name}
                    </div>
                  </div>
                )}

                <div className="mt-6 flex items-center gap-3 justify-end">
                  <button data-testid="reg-import-dryrun" className="btn-ghost" disabled={!file || busy} onClick={() => upload(true)}>
                    <Eye size={14} /> Preview
                  </button>
                  <button data-testid="reg-import-commit" className="btn-red" disabled={!file || busy} onClick={() => upload(false)}>
                    <Upload size={14} /> {busy ? "Importing…" : "Import now"}
                  </button>
                </div>
              </>
            )}

            {result && (
              <div className="mt-6" data-testid="reg-import-result">
                <div className="hairline p-5 flex items-center gap-3" style={{ background: "rgba(63,191,127,0.06)" }}>
                  <CheckCircle2 size={22} style={{ color: "var(--green)" }} />
                  <div>
                    <div className="font-serif text-lg">Import complete</div>
                    <div className="mono-label" style={{ color: "var(--taupe)" }}>
                      {result.inserted} new · {result.updated} updated · {result.skipped} skipped
                    </div>
                  </div>
                </div>
                {result.errors?.length > 0 && (
                  <div className="mt-4 max-h-[200px] overflow-y-auto text-xs">
                    {result.errors.slice(0, 20).map((e, i) => (
                      <div key={i} className="mono-label" style={{ color: "var(--amber)" }}>Row {e.row}: {e.message}</div>
                    ))}
                  </div>
                )}
                <div className="mt-6 flex items-center gap-3 justify-end">
                  <button className="btn-ghost" onClick={reset}>Import another</button>
                  <button className="btn-red" onClick={() => setOpen(false)}>Done</button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
